import { apiClient, isFeatureEnabled } from "./authService";
import { IPageable } from "../types/table/interfaces.type";

/**
 * AMOUNT earns on money spent (the earn usd -> points rate); SESSION earns a
 * flat number per paid session, with packages earning once per session they
 * include.
 */
export type LoyaltyEarnMode = "AMOUNT" | "SESSION";

export interface LoyaltySettingsPayload {
  organizationId: string;
  earn?: {
    usd: number;
    points: number;
  };
  earnMode?: LoyaltyEarnMode;
  /** Integer >= 0; the backend rejects anything below 1 in SESSION mode. */
  pointsPerSession?: number;
  redeem?: {
    points: number;
    discount: number;
    type?: "PERCENT" | "FIXED" | string;
  };
  type?: "PERCENT" | "FIXED" | string;
  isLoyalty?: boolean;
  isShare?: boolean;
  onFullInvoice?: boolean;
  isFixedShare?: boolean;
  shareDivisor?: number;
  businessShareSettings?: BusinessShareSettingPayload[];
}

/**
 * Per-business override of the fixed split. A padel court seats 4 and a
 * football pitch 12, so the divisor is configured per business and the
 * org-level values are only the fallback for businesses with no row.
 */
export interface BusinessShareSettingPayload {
  businessId: string;
  isFixedShare: boolean;
  /** null inherits the organization's number. */
  shareDivisor: number | null;
}

export interface BusinessShareSettingResponse {
  business_id?: string;
  businessId?: string;
  is_fixed_share?: boolean;
  isFixedShare?: boolean;
  share_divisor?: number | null;
  shareDivisor?: number | null;
}

export interface LoyaltyPointsPayload {
  tenantId: string;
  userId: string;
  points: number;
}

export interface LoyaltyRedeemPayload {
  organizationId: string;
  userId: string;
  points: number;
}

export interface LoyaltyTransferPayload {
  organizationId: string;
  fromUserId: string;
  toUserId: string;
  points: number;
}

export interface LoyaltyBalanceResponse {
  balance: number;
}

export interface LoyaltyUserResponse {
  id: string;
  coreUserId?: string;
  tenantId?: string;
  balance?: number;
  points?: number;
  name?: string;
  full_name?: string | null;
  phone?: string;
  mobile?: string;
  email?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface LoyaltyPreviewResponse {
  userId: string;
  balance: number;
  pointsToEarn: number;
  totalPointsBeforeShare?: number;
  isFixedShare?: boolean;
  shareDivisor?: number | null;
  /**
   * How many ways the quote was actually divided — correct in both modes, so
   * use it for the explanatory line instead of deciding client-side.
   */
  splitBetween?: number | null;
  /**
   * One entry per participantId actually sent on the request, each carrying
   * that person's own computed points (fixed divisor or headcount, whichever
   * applies) — render these directly rather than dividing pointsToEarn again.
   * An unfilled fixed-divisor slot with no participantId sent simply has no
   * entry here.
   */
  shares?: Array<{
    userId?: string;
    user_id?: string;
    points?: number;
    totalPoints?: number;
    total_points?: number;
  }>;
  pointsToRedeem: number;
  projectedBalanceAfterPayment: number;
  discountPercent: number;
  redeemRuleDiscountPercent?: number;
  discountType: "PERCENT" | "FIXED" | string;
  isLoyaltyEnabled: boolean;
}

export interface LoyaltySettingsResponse {
  configured?: boolean;
  is_loyalty?: boolean;
  isLoyalty?: boolean;
  is_share?: boolean;
  isShare?: boolean;
  on_full_invoice?: boolean;
  onFullInvoice?: boolean;
  is_fixed_share?: boolean;
  isFixedShare?: boolean;
  share_divisor?: number | null;
  shareDivisor?: number | null;
  // Only configured businesses appear here — a business that is missing is
  // inheriting the org-level values, not an error.
  business_share_settings?: BusinessShareSettingResponse[];
  businessShareSettings?: BusinessShareSettingResponse[];
  earn?: {
    usd?: number;
    points?: number;
  };
  earn_usd?: number;
  earnUsd?: number;
  earn_points?: number;
  earnPoints?: number;
  earn_mode?: LoyaltyEarnMode | string;
  earnMode?: LoyaltyEarnMode | string;
  earn_points_per_session?: number | null;
  earnPointsPerSession?: number | null;
  redeem?: {
    points?: number;
    discount?: number;
    type?: "PERCENT" | "FIXED" | string;
  };
  redeem_points?: number;
  redeemPoints?: number;
  redeem_discount?: number;
  redeemDiscount?: number;
  type?: "PERCENT" | "FIXED" | string;
}

const disabledLoyaltySettings: LoyaltySettingsResponse = {
  configured: false,
  is_loyalty: false,
  isLoyalty: false,
  is_share: false,
  isShare: false,
  on_full_invoice: false,
  onFullInvoice: false,
  is_fixed_share: false,
  isFixedShare: false,
  share_divisor: null,
  shareDivisor: null,
  business_share_settings: [],
  businessShareSettings: [],
  earn: { usd: 0, points: 0 },
  earn_usd: 0,
  earnUsd: 0,
  earn_points: 0,
  earnPoints: 0,
  earn_mode: "AMOUNT",
  earnMode: "AMOUNT",
  earn_points_per_session: 0,
  earnPointsPerSession: 0,
  redeem: { points: 0, discount: 0, type: "PERCENT" },
  redeem_points: 0,
  redeemPoints: 0,
  redeem_discount: 0,
  redeemDiscount: 0,
  type: "PERCENT",
};

export interface LoyaltyEnabledPayload {
  organizationId: string;
  isLoyalty: boolean;
}

export interface FullInvoiceEnabledPayload {
  organizationId: string;
  onFullInvoice: boolean;
}

export interface SharePointsEnabledPayload {
  organizationId: string;
  isShare: boolean;
}

export interface FixedSharePayload {
  organizationId: string;
  isFixedShare: boolean;
  /**
   * Headcount the points are divided by. Integer >= 2 — padel is 4, squad
   * sports run higher. Only meaningful while isFixedShare is on.
   */
  shareDivisor?: number;
}

export const FIXED_SHARE_MIN_DIVISOR = 2;
export const FIXED_SHARE_DEFAULT_DIVISOR = 4;
// The API takes any integer >= 2; this is the admin-screen cap, two digits.
export const FIXED_SHARE_MAX_DIVISOR = 16;

export interface ResolvedShareSetting {
  isFixedShare: boolean;
  shareDivisor: number | null;
}

const readShareDivisor = (value: unknown): number | null => {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric >= FIXED_SHARE_MIN_DIVISOR
    ? numeric
    : null;
};

export const normalizeBusinessShareSettings = (
  settings?: LoyaltySettingsResponse | null,
): BusinessShareSettingPayload[] => {
  const rows =
    settings?.business_share_settings ?? settings?.businessShareSettings ?? [];
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => ({
      businessId: String(row?.business_id ?? row?.businessId ?? "").trim(),
      isFixedShare: Boolean(row?.is_fixed_share ?? row?.isFixedShare),
      shareDivisor: readShareDivisor(row?.share_divisor ?? row?.shareDivisor),
    }))
    .filter((row) => Boolean(row.businessId));
};

/**
 * Applies the inheritance table: a business uses its own row where it has one,
 * and falls back to the organization for anything it leaves unset.
 */
export const resolveShareSettingForBusiness = (
  settings: LoyaltySettingsResponse | null | undefined,
  businessId?: string | null,
): ResolvedShareSetting => {
  const orgIsFixedShare = Boolean(
    settings?.is_fixed_share ?? settings?.isFixedShare,
  );
  const orgDivisor = readShareDivisor(
    settings?.share_divisor ?? settings?.shareDivisor,
  );

  const row = businessId
    ? normalizeBusinessShareSettings(settings).find(
        (item) => item.businessId === String(businessId),
      )
    : undefined;

  if (!row) {
    return { isFixedShare: orgIsFixedShare, shareDivisor: orgDivisor };
  }

  return {
    isFixedShare: row.isFixedShare,
    shareDivisor: row.shareDivisor ?? orgDivisor,
  };
};

export type LoyaltyRewardType = "PRODUCT" | "SERVICE";

/**
 * Which services a SERVICE reward may be redeemed against.
 * ALL is the default and keeps existing rewards working.
 */
export type LoyaltyServiceScope = "ALL" | "INCLUDE" | "EXCLUDE";

export interface LoyaltyReward {
  id: number;
  tenant_id?: number;
  tenantId?: number;
  title?: string | null;
  description?: string | null;
  image_url?: string | null;
  imageUrl?: string | null;
  redeem_points?: number;
  redeemPoints?: number;
  reward_type?: LoyaltyRewardType;
  rewardType?: LoyaltyRewardType;
  reward_item_id?: string | null;
  rewardItemId?: string | null;
  // A SERVICE reward's free time is the duration of the service it points at.
  duration?: number | null;
  service_scope?: LoyaltyServiceScope;
  serviceScope?: LoyaltyServiceScope;
  // Which services it may be redeemed against, for INCLUDE / EXCLUDE.
  service_ids?: string[];
  serviceIds?: string[];
  quantity: number;
  active: boolean | string | number;
  // Single word, so the API uses the same key in both casings.
  pin?: boolean | string | number;
  is_deleted?: boolean;
  isDeleted?: boolean;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
}

export const getRewardServiceScope = (
  reward: any
): LoyaltyServiceScope => {
  const scope = String(
    reward?.service_scope ?? reward?.serviceScope ?? ""
  ).toUpperCase();
  return scope === "INCLUDE" || scope === "EXCLUDE" ? scope : "ALL";
};

export const getRewardServiceIds = (reward: any): string[] => {
  const ids = reward?.service_ids ?? reward?.serviceIds;
  if (!Array.isArray(ids)) return [];
  return ids.map((id: any) => String(id || "")).filter(Boolean);
};

/**
 * Whether a booked service passes a reward's include/exclude list. ALL — the
 * default, and what every existing reward has — allows everything.
 */
export const isServiceAllowedForReward = (
  reward: any,
  serviceId: string
): boolean => {
  const scope = getRewardServiceScope(reward);
  if (scope === "ALL") return true;

  const ids = getRewardServiceIds(reward);
  // A scoped reward with no ids saved would otherwise match nothing at all;
  // treat it as unscoped rather than making it silently unusable.
  if (ids.length === 0) return true;

  const isListed = ids.includes(String(serviceId || ""));
  return scope === "INCLUDE" ? isListed : !isListed;
};

export interface LoyaltyRewardPayload {
  title?: string;
  description?: string;
  imageUrl?: string;
  image?: File | null;
  redeemPoints: number;
  rewardType: LoyaltyRewardType;
  /** For a SERVICE reward this service's duration is the free time. */
  rewardItemId?: string;
  /** SERVICE rewards only. INCLUDE and EXCLUDE need at least one id. */
  serviceScope?: LoyaltyServiceScope;
  serviceIds?: string[];
  quantity: number;
  active?: boolean;
  pin?: boolean;
}

const buildRewardRequestBody = (
  body: Partial<LoyaltyRewardPayload>
): Partial<LoyaltyRewardPayload> | FormData => {
  if (!(body.image instanceof File)) {
    const { image, ...rest } = body;
    void image;
    const payload: Record<string, unknown> = {};
    Object.entries(rest).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (value === "" && key === "rewardItemId") return;
      payload[key] = value;
    });
    return payload as Partial<LoyaltyRewardPayload>;
  }

  const formData = new FormData();
  Object.entries(body).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (key === "image" && value instanceof File) {
      formData.append("image", value);
      return;
    }
    // serviceIds is a list; String() on an array would send "a,b" and lose it.
    if (Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
      return;
    }
    formData.append(key, String(value));
  });
  return formData;
};

export const createReward = async (body: LoyaltyRewardPayload) => {
  const res = await apiClient.post(
    "/loyalty/rewards",
    buildRewardRequestBody(body)
  );
  return res.data;
};

export interface ResolvedServiceReward {
  /** The reward service's duration — the free time it buys. */
  durationMinutes?: number;
  serviceDurationMinutes?: number;
  discountPercent?: number;
  /** The exact figure the invoice line must carry. */
  discountAmount?: number;
  redeemPoints?: number;
}

/**
 * Prices a SERVICE reward against the line it will discount. The backend
 * validates the saved line against this, so never work the percentage out
 * locally — ask for it.
 *
 * Always send the line's own price and quantity: without them the backend
 * falls back to the catalogue price and the figure overshoots a repriced or
 * multi-quantity line. `discountAmount` covers the whole line, never one unit.
 */
export const resolveServiceReward = async (
  rewardId: number | string,
  {
    serviceId,
    price,
    qty,
  }: {
    serviceId: string;
    price?: number;
    qty?: number;
  }
): Promise<ResolvedServiceReward> => {
  const res = await apiClient.get<ResolvedServiceReward>(
    `/loyalty/rewards/${encodeURIComponent(String(rewardId))}/resolve`,
    {
      params: {
        serviceId,
        ...(Number.isFinite(price) && (price as number) >= 0 ? { price } : {}),
        ...(Number.isFinite(qty) && (qty as number) > 0 ? { qty } : {}),
      },
    }
  );
  return res.data;
};

export const getRewards = async (): Promise<LoyaltyReward[]> => {
  if (!isFeatureEnabled("LOYALTY")) return [];
  const res = await apiClient.get<LoyaltyReward[] | { rewards?: LoyaltyReward[] }>(
    "/loyalty/rewards"
  );
  const data = res.data as any;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rewards)) return data.rewards;
  return [];
};

export const getReward = async (id: number | string): Promise<LoyaltyReward> => {
  const res = await apiClient.get<LoyaltyReward>(
    `/loyalty/rewards/${encodeURIComponent(String(id))}`
  );
  return res.data;
};

export const updateReward = async (
  id: number | string,
  body: Partial<LoyaltyRewardPayload>
) => {
  const res = await apiClient.put(
    `/loyalty/rewards/${encodeURIComponent(String(id))}`,
    buildRewardRequestBody(body)
  );
  return res.data;
};

export const setRewardActive = async (
  id: number | string,
  active: boolean
) => {
  const res = await apiClient.patch(
    `/loyalty/rewards/${encodeURIComponent(String(id))}/active`,
    { active }
  );
  return res.data;
};

export const setRewardPin = async (id: number | string, pin: boolean) => {
  const res = await apiClient.patch(
    `/loyalty/rewards/${encodeURIComponent(String(id))}/pin`,
    { pin }
  );
  return res.data;
};

export const deleteReward = async (id: number | string) => {
  const res = await apiClient.delete(
    `/loyalty/rewards/${encodeURIComponent(String(id))}`
  );
  return res.data;
};

export const saveLoyaltySettings = async (
  payload: LoyaltySettingsPayload
) => {
  const res = await apiClient.post("/loyalty/settings", payload);
  return res.data;
};

export const getLoyaltySettings = async (
  organizationId: string
): Promise<LoyaltySettingsResponse> => {
  // With LOYALTY off the endpoint only ever answers 403, and every caller reads
  // this shape to mean "no loyalty" anyway — so don't make the call.
  if (!isFeatureEnabled("LOYALTY")) return disabledLoyaltySettings;
  try {
    const res = await apiClient.get<LoyaltySettingsResponse>(
      "/loyalty/settings",
      {
        headers: {
          "x-organization-id": organizationId,
        },
      }
    );
    return res.data;
  } catch (error) {
    return disabledLoyaltySettings;
  }
};

export const updateLoyaltyEnabled = async ({
  organizationId,
  isLoyalty,
}: LoyaltyEnabledPayload) => {
  const res = await apiClient.post(
    "/loyalty/settings",
    {
      organizationId,
      isLoyalty,
    },
    {
      headers: {
        "x-organization-id": organizationId,
      },
    }
  );
  return res.data;
};

export const updateFullInvoiceEnabled = async ({
  organizationId,
  onFullInvoice,
}: FullInvoiceEnabledPayload) => {
  const res = await apiClient.post(
    "/loyalty/settings",
    {
      organizationId,
      onFullInvoice,
    },
    {
      headers: {
        "x-organization-id": organizationId,
      },
    }
  );
  return res.data;
};

export const updateSharePointsEnabled = async ({
  organizationId,
  isShare,
}: SharePointsEnabledPayload) => {
  const res = await apiClient.post(
    "/loyalty/settings",
    {
      organizationId,
      isShare,
    },
    {
      headers: {
        "x-organization-id": organizationId,
      },
    }
  );
  return res.data;
};

export const updateFixedShareSettings = async ({
  organizationId,
  isFixedShare,
  shareDivisor,
}: FixedSharePayload) => {
  const res = await apiClient.post(
    "/loyalty/settings",
    {
      organizationId,
      isFixedShare,
      ...(shareDivisor === undefined ? {} : { shareDivisor }),
    },
    {
      headers: {
        "x-organization-id": organizationId,
      },
    }
  );
  return res.data;
};

export const saveBusinessShareSettings = async (
  organizationId: string,
  businessShareSettings: BusinessShareSettingPayload[]
) => {
  // Accepted on its own — no need to resend earn/redeem to save a divisor.
  const res = await apiClient.post(
    "/loyalty/settings",
    {
      organizationId,
      businessShareSettings,
    },
    {
      headers: {
        "x-organization-id": organizationId,
      },
    }
  );
  return res.data;
};

export const initializeExistingLoyaltyUsers = async (
  organizationId: string
) => {
  const res = await apiClient.post(
    "/loyalty/temporary/initialize-existing-users",
    { organizationId },
    {
      headers: {
        "x-organization-id": organizationId,
      },
    }
  );
  return res.data;
};

export const addLoyaltyPoints = async (payload: LoyaltyPointsPayload) => {
  const res = await apiClient.post("/loyalty/manual/add", payload);
  return res.data;
};

export const removeLoyaltyPoints = async (payload: LoyaltyPointsPayload) => {
  const res = await apiClient.post("/loyalty/manual/remove", payload);
  return res.data;
};

export const redeemLoyaltyPoints = async ({
  organizationId,
  userId,
  points,
}: LoyaltyRedeemPayload) => {
  const res = await apiClient.post(
    "/loyalty/redeem",
    {
      organizationId,
      userId,
      points,
    },
    {
      headers: {
        "x-organization-id": organizationId,
      },
    }
  );
  return res.data;
};

export const transferLoyaltyPoints = async ({
  organizationId,
  fromUserId,
  toUserId,
  points,
}: LoyaltyTransferPayload) => {
  const res = await apiClient.post(
    "/loyalty/transfer",
    {
      organizationId,
      fromUserId,
      toUserId,
      points,
    },
    {
      headers: {
        "x-organization-id": organizationId,
      },
    }
  );
  return res.data;
};

export const getLoyaltyBalance = async (
  tenantId: string,
  userId: string
): Promise<LoyaltyBalanceResponse> => {
  void tenantId;
  const res = await apiClient.get<LoyaltyBalanceResponse>(
    `/loyalty/balance/${encodeURIComponent(userId)}`
  );
  return res.data;
};

export const getClientLoyaltyBalance = async (
  userId: string
): Promise<LoyaltyBalanceResponse> => {
  const res = await apiClient.get<LoyaltyBalanceResponse>(
    `/loyalty/balance/${encodeURIComponent(userId)}`
  );
  return res.data;
};

/**
 * Balance range, both ends inclusive. Whole numbers >= 0; either may be left
 * out, and min > max is a 400.
 */
export interface LoyaltyUsersFilter {
  minPoints?: number;
  maxPoints?: number;
}

export const getLoyaltyUsers = async (
  organizationId: string,
  { minPoints, maxPoints }: LoyaltyUsersFilter = {}
): Promise<LoyaltyUserResponse[]> => {
  if (!isFeatureEnabled("LOYALTY")) return [];
  const res = await apiClient.get<LoyaltyUserResponse[]>(
    "/loyalty/users",
    {
      headers: {
        "x-organization-id": organizationId,
      },
      params: {
        ...(minPoints === undefined ? {} : { minPoints }),
        ...(maxPoints === undefined ? {} : { maxPoints }),
      },
    }
  );
  return Array.isArray(res.data) ? res.data : [];
};

export interface LoyaltyUsersPageQuery extends LoyaltyUsersFilter {
  page: number;
  size: number;
  /** Matches client name, phone or email. */
  search?: string;
  /** `points,desc`, `points,asc`, `createdAt,desc` or `createdAt,asc`. */
  sort?: string;
}

const emptyPage = <T>(page: number, size: number): IPageable<T> => ({
  content: [],
  number: page,
  size,
  totalElements: 0,
  totalPages: 1,
});

/** One page of loyalty users; the server does the search and the paging. */
export const getLoyaltyUsersPage = async (
  organizationId: string,
  { page, size, search, sort, minPoints, maxPoints }: LoyaltyUsersPageQuery
): Promise<IPageable<LoyaltyUserResponse>> => {
  if (!isFeatureEnabled("LOYALTY")) return emptyPage(page, size);
  const trimmedSearch = search?.trim();
  const res = await apiClient.get<IPageable<LoyaltyUserResponse>>(
    "/loyalty/users",
    {
      headers: {
        "x-organization-id": organizationId,
      },
      params: {
        page,
        size,
        ...(trimmedSearch ? { search: trimmedSearch } : {}),
        ...(sort ? { sort } : {}),
        ...(minPoints === undefined ? {} : { minPoints }),
        ...(maxPoints === undefined ? {} : { maxPoints }),
      },
    }
  );
  const data = res.data;
  return data && Array.isArray(data.content) ? data : emptyPage(page, size);
};

export const getLoyaltyPreview = async ({
  organizationId,
  userId,
  amount,
  redeemPoints = 0,
  allowWhenDisabled = false,
  businessId,
  participants,
  participantIds,
}: {
  organizationId: string;
  userId: string;
  amount: number;
  redeemPoints?: number;
  allowWhenDisabled?: boolean;
  /**
   * Business of the court being booked. Without it the preview falls back to
   * the org divisor and a football booking quotes the padel number.
   */
  businessId?: string | null;
  /**
   * 1 + selected guests. With sharing on but fixed dividing off the preview
   * cannot know the headcount otherwise, and quotes the undivided total.
   * Superseded by participantIds when given — kept as a back-compat fallback.
   */
  participants?: number | null;
  /**
   * The actual client IDs sharing the invoice (payer + guests). Lets the API
   * return a `shares` entry per person instead of us dividing client-side.
   */
  participantIds?: string[] | null;
}): Promise<LoyaltyPreviewResponse> => {
  const res = await apiClient.get<LoyaltyPreviewResponse>(
    `/loyalty/preview/${encodeURIComponent(userId)}`,
    {
      headers: {
        "x-organization-id": organizationId,
      },
      params: {
        amount,
        redeemPoints,
        allowWhenDisabled,
        ...(businessId ? { businessId } : {}),
        ...(participants && participants > 0 ? { participants } : {}),
        ...(participantIds && participantIds.length > 0
          ? { participantIds: participantIds.join(",") }
          : {}),
      },
    }
  );
  return res.data;
};

export const getLoyaltyTransactions = async (
  organizationId: string,
  userId: string
): Promise<any[]> => {
  if (!isFeatureEnabled("LOYALTY")) return [];
  const res = await apiClient.get(
    `/loyalty/transactions/${encodeURIComponent(userId)}`,
    {
      headers: {
        "x-organization-id": organizationId,
      },
    }
  );
  return Array.isArray(res.data) ? res.data : [];
};

export const getClientLoyaltyTransactions = async (
  userId: string,
  limit = 3
): Promise<any[]> => {
  if (!isFeatureEnabled("LOYALTY")) return [];
  const res = await apiClient.get(
    `/loyalty/transactions/${encodeURIComponent(userId)}`,
    {
      params: { limit },
    }
  );
  const data = res.data as any;
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.transactions)
      ? data.transactions
      : [];
  return list.slice(0, limit);
};

export const getAppointmentPointShares = async (
  organizationId: string,
  appointmentId: string
): Promise<any[]> => {
  if (!isFeatureEnabled("LOYALTY")) return [];
  const res = await apiClient.get(
    `/loyalty/share-points/${encodeURIComponent(appointmentId)}`,
    {
      headers: {
        "x-organization-id": organizationId,
      },
    }
  );
  return Array.isArray(res.data) ? res.data : [];
};
